import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button, { ButtonVariant } from '@repo/ui/button'
import { ExclamationMarkIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { generateFinalCostCenterOlPayments } from '../../../../api/center-ol-payments/generate-final-cost-center-ol-payments'
import { getCenterOlPayments } from '../../../../api/center-ol-payments/get-center-ol-payments'
import { getCenter } from '../../../../api/centers/get-center'
import { getCenterActions } from '../../../../api/centers/get-center-actions'
import { openActivities } from '../../../../api/centers/open-center-activities'
import { openCenterCosts } from '../../../../api/centers/open-center-costs'
import { openCenterFinalCosts } from '../../../../api/centers/open-center-final-costs'
import { sendActivitiesToLec } from '../../../../api/centers/send-center-activities-to-lec'
import { sendCostsToLec } from '../../../../api/centers/send-center-costs-to-lec'
import {
  CostMissingInvoices,
  getCenterCostMissingDocuments,
  sendFinalCostsToLec
} from '../../../../api/centers/send-center-final-costs-to-lec'
import { validateActivities } from '../../../../api/centers/validate-center-activities'
import { validateCenterCosts } from '../../../../api/centers/validate-center-costs'
import { validateCenterFinalCosts } from '../../../../api/centers/validate-center-final-costs'
import { Paths } from '../../../../lib/constants/path'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CenterStatus } from '../../../../lib/types/enums/center/center-status'
import { CenterOlPaymentStatusEnum } from '../../../../lib/types/enums/center-ol-payment/center-ol-payment-status.enum'
import { CenterOlPaymentTypeEnum } from '../../../../lib/types/enums/center-ol-payment/center-ol-payment-type.enum'
import { OLTypeEnum } from '../../../../lib/types/enums/ol/ol-type.enum'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { Center } from '../../../../lib/types/models/center/center'
import { DataProps } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { LocaleKeys } from '../../../../locale/translation-types'
import { CenterMissingInvoices } from './center.ui'

type CenterStatusItem = {
  action: 'validate' | 'send-to-lec' | 'open'
  costType: 'original-cost' | 'final-cost' | 'activities-cost'
  label: LocaleKeys
  roles: RoleEnum[]
  olTypes: OLTypeEnum[]
  variant?: ButtonVariant
  condition?: (centerId: number) => Promise<boolean> | boolean
  fn: (centerId: number) => Promise<unknown> | void
}

const centerCostsStateMapping: Record<CenterStatus, CenterStatusItem[]> = {
  OPEN: [
    {
      action: 'send-to-lec',
      costType: 'original-cost',
      label: 'center.costs.status.send.to.lec',
      roles: [RoleEnum.Ol],
      olTypes: [OLTypeEnum.LEC],
      variant: 'primary',
      fn: sendCostsToLec
    },
    {
      action: 'validate',
      costType: 'original-cost',
      label: 'center.costs.validate',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'primary',
      fn: validateCenterCosts
    },
    {
      action: 'send-to-lec',
      costType: 'final-cost',
      label: 'center.costs.status.send.to.lec',
      roles: [RoleEnum.Ol],
      olTypes: [OLTypeEnum.LEC],
      variant: 'primary',
      fn: (centerId: number) => sendFinalCostsToLec(centerId, { confirmPayment: true })
    },
    {
      action: 'validate',
      costType: 'final-cost',
      label: 'center.costs.validate',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'primary',
      fn: validateCenterFinalCosts
    },
    {
      action: 'send-to-lec',
      costType: 'activities-cost',
      label: 'center.costs.status.send.to.lec',
      roles: [RoleEnum.Ol],
      olTypes: [OLTypeEnum.LEC],
      variant: 'primary',
      fn: sendActivitiesToLec
    },
    {
      action: 'validate',
      costType: 'activities-cost',
      label: 'center.costs.validate',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'primary',
      fn: validateActivities
    }
  ],
  SENT_TO_LEC: [
    {
      action: 'open',
      costType: 'original-cost',
      label: 'center.costs.reopen',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'ghost',
      fn: openCenterCosts
    },
    {
      action: 'validate',
      costType: 'original-cost',
      label: 'center.costs.validate',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'primary',
      fn: validateCenterCosts
    },
    {
      action: 'open',
      costType: 'final-cost',
      label: 'center.costs.reopen',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'ghost',
      fn: openCenterFinalCosts
    },
    {
      action: 'validate',
      costType: 'final-cost',
      label: 'center.costs.validate',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'primary',
      fn: validateCenterFinalCosts
    },

    {
      action: 'open',
      costType: 'activities-cost',
      label: 'center.costs.reopen',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'ghost',
      fn: openActivities
    },
    {
      action: 'validate',
      costType: 'activities-cost',
      label: 'center.costs.validate',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'primary',
      fn: validateActivities
    }
  ],
  VALIDATED: [
    {
      action: 'send-to-lec',
      costType: 'original-cost',
      label: 'center.costs.unvalidate',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'ghost',
      fn: sendCostsToLec
    },
    {
      action: 'send-to-lec',
      costType: 'final-cost',
      label: 'center.costs.unvalidate',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'ghost',
      fn: (centerId: number) => sendFinalCostsToLec(centerId, { confirmPayment: true })
    },
    {
      action: 'send-to-lec',
      costType: 'activities-cost',
      label: 'center.costs.unvalidate',
      roles: [RoleEnum.ADMIN],
      olTypes: [OLTypeEnum.LEC],
      variant: 'ghost',
      fn: sendActivitiesToLec
    }
  ]
}

const CenterCostItemButton: React.FC<
  CenterStatusItem & {
    centerId: number
    disabled?: boolean
    onSuccess: () => Promise<unknown> | void
  }
> = ({ label, centerId, variant, disabled, condition, onSuccess, fn }) => {
  const { t } = useTranslate()
  const [loading, setLoading] = useState(false)

  return (
    <Button
      label={t(label)}
      disabled={disabled}
      loading={loading}
      variant={variant}
      onClick={async () => {
        setLoading(true)
        if (condition) {
          const isConditionMet = await condition(centerId)
          if (!isConditionMet) {
            setLoading(false)
            return
          }
        }
        try {
          await fn(centerId)
        } catch (error) {
          showAxiosError(error as Error)
        }
        await onSuccess()
        setLoading(false)
      }}
    />
  )
}

type Props = {
  costType: 'original-cost' | 'final-cost' | 'activities-cost'
  disabled?: boolean
  finalTransferCost?: boolean
}

export function CenterCostsStatus(props: DataProps<Center, Props>) {
  const { costType, disabled, finalTransferCost } = props
  const { user } = useAuth()
  const { t } = useTranslate()
  const { navigate } = useNavigator()

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'missing-invoices',
    CostMissingInvoices[]
  >(['missing-invoices'])

  const {
    id: centerId,
    data: center,
    refetch: refetchCenter
  } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const generateFinalCostPaymentsMutation = useMutation({
    onError: showAxiosError,
    mutationKey: ['generate-final-cost-center-ol-payment'],
    mutationFn: generateFinalCostCenterOlPayments,
    onSuccess: () => {
      toast.success('Les virements ont été créés avec succès')
    }
  })

  const { data: payments } = useQuery({
    queryKey: ['center-ol-payments', centerId],
    queryFn: () => getCenterOlPayments({ filter: { centerId: centerId } }),
    enabled: !!centerId && [RoleEnum.ADMIN, RoleEnum.Ol].includes(user.role)
  })

  const { data: centerActions, refetch: refetchCenterActions } = useQuery({
    queryKey: ['center-actions', centerId],
    queryFn: () => getCenterActions(centerId),
    enabled: !!centerId
  })

  const status = useMemo(() => {
    switch (costType) {
      case 'original-cost':
        return center?.costsStatus
      case 'final-cost':
        return center?.finalCostsStatus
      case 'activities-cost':
        return center?.activitiesStatus
      default:
        return null
    }
  }, [costType, center?.activitiesStatus, center?.costsStatus, center?.finalCostsStatus])

  if (!center || !user || !status) return null
  return (
    <div className={'flex justify-end flex-row items-center gap-4'}>
      {/* the activity program tab already has the back button at the top of the center page */}
      {costType !== 'activities-cost' && (
        <Button
          variant={'ghost'}
          label={t('ol.center.back.button')}
          disabled={disabled}
          onClick={() =>
            navigate(
              user.role === RoleEnum.Ol
                ? Paths.OL_PERSONAL_SPACE_CENTER_LIST_PAGE
                : Paths.OL_CENTER_LIST_PAGE
            )
          }
        />
      )}

      {costType === 'activities-cost' &&
        status !== CenterStatus.OPEN &&
        user.role === RoleEnum.Ol && (
          <Badge variant={'primary'} text={t('ol.center.program.activities.sent.to.lec.badge')} />
        )}

      {centerCostsStateMapping[status]
        .filter(
          (item) =>
            item.roles.includes(user.role) &&
            item.costType === costType &&
            (center?.ol?.type ? item.olTypes.includes(center.ol.type) : false) &&
            // the costs cannot be unblocked once the before stay payment has been validated
            (item.action !== 'send-to-lec' ||
              item.costType !== 'original-cost' ||
              status !== CenterStatus.VALIDATED ||
              centerActions?.canUnblockCosts === true)
        )
        .map((item) => (
          <CenterCostItemButton
            key={item.label}
            disabled={disabled}
            {...item}
            {...(item.costType === 'final-cost' &&
              item.action === 'send-to-lec' && {
                condition: async (centerId: number) => {
                  if (user.role === RoleEnum.ADMIN) return true
                  const missingInvoices = await getCenterCostMissingDocuments(centerId)
                  if (missingInvoices.length > 0) {
                    toggleModal('missing-invoices', missingInvoices)
                    return false
                  }
                  return true
                }
              })}
            centerId={centerId}
            onSuccess={() => Promise.all([refetchCenter?.(), refetchCenterActions()])}
          />
        ))}

      {finalTransferCost && (
        <Button
          variant={'primary'}
          label={t('center.costs.final.final.transfer.cost')}
          onClick={() => {
            generateFinalCostPaymentsMutation.mutate([centerId])
          }}
          loading={generateFinalCostPaymentsMutation.isPending}
          // disable the button if
          // - the center final costs is not validated
          // - there is no validated center cost payment
          // - there is already a final cost payment
          disabled={
            center?.finalCostsStatus !== CenterStatus.VALIDATED ||
            !payments?.data?.some(
              (payment) =>
                payment.type === CenterOlPaymentTypeEnum.COST &&
                payment.status === CenterOlPaymentStatusEnum.VALIDATED
            ) ||
            payments?.data?.some((payment) => payment.type === CenterOlPaymentTypeEnum.FINAL)
          }
        />
      )}

      {selectedData && (
        <Modal
          variant="destructive"
          icon={ExclamationMarkIcon}
          isOpen={modalState['missing-invoices']}
          size="medium"
          onCancel={() => closeModel('missing-invoices')}
          title={t('center.costs.missing.invoices.title')}
          hideActions
          hasBorderBottom={false}
        >
          <CenterMissingInvoices invoices={selectedData} className={'p-9'} />
        </Modal>
      )}
    </div>
  )
}
